<template>
  <AdminAssetTable
    :config="agentsConfig"
    :items="agents"
    :users="users"
    :api-service="agentsApiService"
    @refresh="loadAgents"
    @created="handleAgentCreated"
    @updated="handleAgentUpdated"
    @deleted="handleAgentDeleted"
  />
</template>

<script setup lang="ts">
import type { Entity } from '@/api/types/core/common';
import type { User } from '@/api/types/core/users';
import type { Agent } from '@/api/types/entities/agent/types';
import AdminAssetTable from '@/components/admin/AdminAssetTable.vue';
import { ADMIN_ASSET_CONFIGS } from '@/configs/adminAssetConfigs';
import { onMounted, ref } from 'vue';
import { agents as agentsApi } from '../../api/agents';
import { fetchAllUsers } from '../../api/users';

// Configuration
const agentsConfig = ADMIN_ASSET_CONFIGS.agents;

// État
const agents = ref<Agent[]>([]);
const users = ref<User[]>([]);
const loading = ref(false);

// Service API adapté pour AdminAssetTable
const agentsApiService = {
  async getAll(): Promise<Entity[]> {
    return await agentsApi.getAllAdminWithDeleted();
  },
  
  async create(data: any): Promise<Entity> {
    const agentData = {
      user_id: data.user_id,
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      is_legal_entity: data.is_legal_entity || false,
      idref_id: data.idref_id || '',
      roles: data.roles || '',
      group: data.group || '',
      affiliation: data.affiliation || '',
      affiliation_id: data.affiliation_id || '',
      is_public: data.is_public || true
    };
    return await agentsApi.createAgent(agentData);
  },
  
  async update(uuid: string, data: any): Promise<Entity> {
    return await agentsApi.updateAgent(uuid, data);
  },
  
  async delete(uuid: string): Promise<void> {
    await agentsApi.deleteAgent(uuid);
  },
  async hardDelete(uuid: string): Promise<void> {
    await agentsApi.hardDeleteAgent(uuid);
  },
  async restore(uuid: string): Promise<void> {
    await agentsApi.restoreAgent(uuid);
  }
};

// Méthodes
const loadAgents = async (): Promise<void> => {
  loading.value = true;
  try {
    agents.value = await agentsApi.getAllAdminWithDeleted();
  } catch (error) {
    console.error('Erreur lors du chargement des agents:', error);
  } finally {
    loading.value = false;
  }
};

const loadUsers = async (): Promise<void> => {
  try {
    users.value = await fetchAllUsers();
  } catch (error) {
    console.error('Erreur lors du chargement des utilisateurs:', error);
  }
};

// Gestionnaires d'événements
const handleAgentCreated = (agent: Entity): void => {
  console.log('Agent créé:', agent);
  // L'actualisation est gérée par l'événement refresh
};

const handleAgentUpdated = (agent: Entity): void => {
  console.log('Agent mis à jour:', agent);
  // L'actualisation est gérée par l'événement refresh
};

const handleAgentDeleted = (uuid: string): void => {
  console.log('Agent supprimé:', uuid);
  // L'actualisation est gérée par l'événement refresh
};

// Lifecycle
onMounted(async () => {
  await Promise.all([
    loadAgents(),
    loadUsers()
  ]);
});
</script> 